import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/Toast';
import ImageUploadField from './ImageUploadField';
import './Forms.css';

export default function AboutUsForm({ apiBase, activeTab }) {
  const toast = useToast();
  
  // Map tab IDs to section configuration
  const sectionMap = {
    'cms-about-hero': { title: 'Hero Section', section: 'hero' },
    'cms-about-principal': { title: 'Principal Message', section: 'principal' },
    'cms-about-values': { title: 'Vision Mission Values', section: 'values' },
    'cms-about-philosophy': { title: 'Philosophy', section: 'philosophy' },
    'cms-about-history': { title: 'History', section: 'history' },
    'cms-about-leadership': { title: 'Leadership', section: 'leadership' }
  };

  const sectionConfig = sectionMap[activeTab] || sectionMap['cms-about-hero'];
  const sectionTitle = sectionConfig.title;
  const sectionName = sectionConfig.section;

  const [content, setContent] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);

  useEffect(() => {
    loadSection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, apiBase]);

  const loadSection = async () => {
    setLoading(true);
    setIsDirty(false);
    try {
      console.log(`[AboutUsForm] Loading section: ${sectionName}`);
      
      // Fetch all about-us sections from /api/content endpoint
      const response = await fetch(
        `${apiBase}/content?menu=about-us`,
        { credentials: 'include' }
      );
      const result = await response.json();
      
      console.log(`[AboutUsForm] Full API Response:`, result);
      
      if (result.ok && Array.isArray(result.data)) {
        console.log(`[AboutUsForm] Available sections:`, result.data.map(s => s.section));
        
        // Find the section object that matches our sectionName
        // Each item in result.data has: { section: 'hero', content: {...}, id, status, ... }
        const foundSection = result.data.find(s => s.section === sectionName);
        
        if (foundSection) {
          console.log(`[AboutUsForm] Found section object:`, foundSection);
          setSectionId(foundSection.id);
          
          // Content is already parsed JSON object from API
          const sectionContent = foundSection.content || { en: {}, id: {} };
          
          console.log(`[AboutUsForm] Content to display:`, sectionContent);
          setContent(sectionContent);
        } else {
          console.warn(`[AboutUsForm] Section "${sectionName}" not found in response`);
          setContent({ en: {}, id: {} });
        }
      } else {
        console.warn(`[AboutUsForm] API response not ok or no data`, result);
        setContent({ en: {}, id: {} });
      }
    } catch (err) {
      console.error('[AboutUsForm] Error loading section:', err);
      toast.error('Failed to load section');
      setContent({ en: {}, id: {} });
    } finally {
      setLoading(false);
    }
  };

  const updateField = (lang, fieldKey, value) => {
    setContent(prev => ({
      ...prev,
      [lang]: {
        ...prev[lang],
        [fieldKey]: value
      }
    }));
    setIsDirty(true);
  };

  const saveSection = async () => {
    if (!sectionId) {
      toast.error('Section ID not found');
      return;
    }

    setIsSaving(true);
    try {
      console.log(`[AboutUsForm] Saving section ${sectionId}:`, content);
      
      // Use /api/content/{id} endpoint to update content
      const response = await fetch(
        `${apiBase}/content/${sectionId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ content })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Save failed');
      }

      const result = await response.json();
      console.log(`[AboutUsForm] ✓ Section saved successfully:`, result);
      toast.success(`✓ ${sectionTitle} saved`);
      setIsDirty(false);
    } catch (err) {
      console.error('[AboutUsForm] Save error:', err);
      toast.error('Save failed: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) return <div className="cms-loading"><div>Loading...</div></div>;

  const enContent = content.en || {};

  return (
    <div className="cms-form">
      <toast.ToastContainer />
      <h2>{sectionTitle}</h2>
      <p>Edit {sectionTitle} content</p>

      {/* Hero Section */}
      {activeTab === 'cms-about-hero' && (
        <div>
          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              value={enContent.title || ''}
              onChange={(e) => updateField('en', 'title', e.target.value)}
              placeholder="Section title"
              maxLength={100}
            />
          </div>

          <div className="form-group">
            <label>Summary</label>
            <textarea
              value={enContent.summary || ''}
              onChange={(e) => updateField('en', 'summary', e.target.value)}
              placeholder="Hero section summary"
              rows="3"
            />
          </div>

          <div className="form-group">
            <label>Hero Slides (Images)</label>
            <p style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
              Add carousel images for the hero section
            </p>
            {(enContent.slides || []).map((slide, idx) => (
              <div key={idx} style={{ border: '1px solid #ddd', padding: '15px', marginBottom: '10px', borderRadius: '4px' }}>
                <ImageUploadField
                  value={slide}
                  onChange={(url) => {
                    const newSlides = [...(enContent.slides || [])];
                    newSlides[idx] = url;
                    updateField('en', 'slides', newSlides);
                  }}
                  label={`Slide ${idx + 1}`}
                  folder="about-us"
                  apiBase={apiBase}
                />
                <button
                  type="button"
                  onClick={() => {
                    const newSlides = (enContent.slides || []).filter((_, i) => i !== idx);
                    updateField('en', 'slides', newSlides);
                  }}
                  style={{ background: '#ff6b6b', color: 'white', padding: '5px 10px', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
                >
                  Remove Slide
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => {
                const newSlides = [...(enContent.slides || []), ''];
                updateField('en', 'slides', newSlides);
              }}
              style={{ background: '#4CAF50', color: 'white', padding: '8px 15px', border: 'none', borderRadius: '3px', cursor: 'pointer', marginTop: '10px' }}
            >
              + Add Slide
            </button>
          </div>

          <div className="btn-group">
            <button
              onClick={saveSection}
              disabled={!isDirty || isSaving}
              className="btn-primary"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {/* Principal Section */}
      {activeTab === 'cms-about-principal' && (
        <div>
          <div className="form-group">
            <label>Name</label>
            <input
              type="text"
              value={enContent.principal_name || ''}
              onChange={(e) => updateField('en', 'principal_name', e.target.value)}
              placeholder="Principal name"
              maxLength={100}
            />
          </div>

          <div className="form-group">
            <label>Credentials</label>
            <input
              type="text"
              value={enContent.principal_credentials || ''}
              onChange={(e) => updateField('en', 'principal_credentials', e.target.value)}
              placeholder="e.g., M.Ed., Ed.D."
              maxLength={100}
            />
          </div>

          <div className="form-group">
            <label>Role</label>
            <input
              type="text"
              value={enContent.principal_role || ''}
              onChange={(e) => updateField('en', 'principal_role', e.target.value)}
              placeholder="e.g., Principal / Head of School"
              maxLength={100}
            />
          </div>

          <div className="form-group">
            <label>Opening</label>
            <textarea
              value={enContent.principal_opening || ''}
              onChange={(e) => updateField('en', 'principal_opening', e.target.value)}
              placeholder="e.g., Dear Parents, Students..."
              rows="2"
            />
          </div>

          <div className="form-group">
            <label>Paragraph One</label>
            <textarea
              value={enContent.principal_paragraph_one || ''}
              onChange={(e) => updateField('en', 'principal_paragraph_one', e.target.value)}
              placeholder="First paragraph of welcome message"
              rows="3"
            />
          </div>

          <div className="form-group">
            <label>Paragraph Two</label>
            <textarea
              value={enContent.principal_paragraph_two || ''}
              onChange={(e) => updateField('en', 'principal_paragraph_two', e.target.value)}
              placeholder="Second paragraph of welcome message"
              rows="3"
            />
          </div>

          <div className="form-group">
            <label>Closing</label>
            <textarea
              value={enContent.principal_closing || ''}
              onChange={(e) => updateField('en', 'principal_closing', e.target.value)}
              placeholder="Closing message"
              rows="2"
            />
          </div>

          <div className="form-group">
            <label>Theme Line</label>
            <input
              type="text"
              value={enContent.principal_theme_line || ''}
              onChange={(e) => updateField('en', 'principal_theme_line', e.target.value)}
              placeholder="Short theme/motto line"
              maxLength={150}
            />
          </div>

          <div className="btn-group">
            <button
              onClick={saveSection}
              disabled={!isDirty || isSaving}
              className="btn-primary"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {/* Values Section */}
      {activeTab === 'cms-about-values' && (
        <div>
          <div className="form-group">
            <label>Kicker</label>
            <input
              type="text"
              value={enContent.values_kicker || ''}
              onChange={(e) => updateField('en', 'values_kicker', e.target.value)}
              placeholder="e.g., Our Direction"
              maxLength={100}
            />
          </div>

          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              value={enContent.values_title || ''}
              onChange={(e) => updateField('en', 'values_title', e.target.value)}
              placeholder="Section title"
              maxLength={100}
            />
          </div>

          <div className="form-group">
            <label>Intro</label>
            <textarea
              value={enContent.values_intro || ''}
              onChange={(e) => updateField('en', 'values_intro', e.target.value)}
              placeholder="Introduction text"
              rows="2"
            />
          </div>

          <div className="form-group">
            <label>Vision Label</label>
            <input
              type="text"
              value={enContent.vision_label || ''}
              onChange={(e) => updateField('en', 'vision_label', e.target.value)}
              placeholder="e.g., Vision"
              maxLength={50}
            />
          </div>

          <div className="form-group">
            <label>Vision Title</label>
            <input
              type="text"
              value={enContent.vision_title || ''}
              onChange={(e) => updateField('en', 'vision_title', e.target.value)}
              placeholder="Vision statement title"
              maxLength={200}
            />
          </div>

          <div className="form-group">
            <label>Vision Description</label>
            <textarea
              value={enContent.vision_description || ''}
              onChange={(e) => updateField('en', 'vision_description', e.target.value)}
              placeholder="School vision statement"
              rows="3"
            />
          </div>

          <div className="form-group">
            <label>Mission Label</label>
            <input
              type="text"
              value={enContent.mission_label || ''}
              onChange={(e) => updateField('en', 'mission_label', e.target.value)}
              placeholder="e.g., Mission"
              maxLength={50}
            />
          </div>

          <div className="form-group">
            <label>Mission Title</label>
            <input
              type="text"
              value={enContent.mission_title || ''}
              onChange={(e) => updateField('en', 'mission_title', e.target.value)}
              placeholder="Mission statement title"
              maxLength={200}
            />
          </div>

          <div className="form-group">
            <label>Mission Description</label>
            <textarea
              value={enContent.mission_description || ''}
              onChange={(e) => updateField('en', 'mission_description', e.target.value)}
              placeholder="School mission statement"
              rows="3"
            />
          </div>

          <div className="form-group">
            <label>Core Values Label</label>
            <input
              type="text"
              value={enContent.core_values_label || ''}
              onChange={(e) => updateField('en', 'core_values_label', e.target.value)}
              placeholder="e.g., Core Values"
              maxLength={50}
            />
          </div>

          <div className="form-group">
            <label>Core Values Title</label>
            <input
              type="text"
              value={enContent.core_values_title || ''}
              onChange={(e) => updateField('en', 'core_values_title', e.target.value)}
              placeholder="Core values statement title"
              maxLength={200}
            />
          </div>

          <div className="form-group">
            <label>Core Values Description</label>
            <textarea
              value={enContent.core_values_description || ''}
              onChange={(e) => updateField('en', 'core_values_description', e.target.value)}
              placeholder="Core values description"
              rows="3"
            />
          </div>

          <div className="btn-group">
            <button
              onClick={saveSection}
              disabled={!isDirty || isSaving}
              className="btn-primary"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {/* Philosophy Section */}
      {activeTab === 'cms-about-philosophy' && (
        <div>
          <div className="form-group">
            <label>Philosophy Items</label>
            <p style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
              Add philosophy pillars with titles, descriptions, and key points
            </p>
            {(enContent.items || []).map((item, idx) => (
              <div key={idx} style={{ border: '1px solid #ddd', padding: '15px', marginBottom: '15px', borderRadius: '4px' }}>
                <div className="form-group">
                  <label>Label/Key</label>
                  <input
                    type="text"
                    value={item.key || ''}
                    onChange={(e) => {
                      const newItems = [...(enContent.items || [])];
                      newItems[idx].key = e.target.value;
                      updateField('en', 'items', newItems);
                    }}
                    placeholder="e.g., faith, fortitude, fulfillment"
                    maxLength={50}
                  />
                </div>
                <div className="form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    value={item.title || ''}
                    onChange={(e) => {
                      const newItems = [...(enContent.items || [])];
                      newItems[idx].title = e.target.value;
                      updateField('en', 'items', newItems);
                    }}
                    placeholder="Philosophy title"
                    maxLength={100}
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={item.description || ''}
                    onChange={(e) => {
                      const newItems = [...(enContent.items || [])];
                      newItems[idx].description = e.target.value;
                      updateField('en', 'items', newItems);
                    }}
                    placeholder="Philosophy description"
                    rows="3"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const newItems = (enContent.items || []).filter((_, i) => i !== idx);
                    updateField('en', 'items', newItems);
                  }}
                  style={{ background: '#ff6b6b', color: 'white', padding: '5px 10px', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => {
                const newItems = [...(enContent.items || []), { key: '', title: '', description: '' }];
                updateField('en', 'items', newItems);
              }}
              style={{ background: '#4CAF50', color: 'white', padding: '8px 15px', border: 'none', borderRadius: '3px', cursor: 'pointer', marginTop: '10px' }}
            >
              + Add Philosophy Item
            </button>
          </div>

          <div className="btn-group">
            <button
              onClick={saveSection}
              disabled={!isDirty || isSaving}
              className="btn-primary"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {/* History Section */}
      {activeTab === 'cms-about-history' && (
        <div>
          <div className="form-group">
            <label>History Timeline</label>
            <p style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
              Add timeline events with years and descriptions
            </p>
            {(enContent.items || []).map((item, idx) => (
              <div key={idx} style={{ border: '1px solid #ddd', padding: '15px', marginBottom: '15px', borderRadius: '4px' }}>
                <div className="form-group">
                  <label>Year</label>
                  <input
                    type="text"
                    value={item.year || ''}
                    onChange={(e) => {
                      const newItems = [...(enContent.items || [])];
                      newItems[idx].year = e.target.value;
                      updateField('en', 'items', newItems);
                    }}
                    placeholder="e.g., 2003"
                    maxLength={50}
                  />
                </div>
                <div className="form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    value={item.title || ''}
                    onChange={(e) => {
                      const newItems = [...(enContent.items || [])];
                      newItems[idx].title = e.target.value;
                      updateField('en', 'items', newItems);
                    }}
                    placeholder="Event title"
                    maxLength={100}
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={item.description || ''}
                    onChange={(e) => {
                      const newItems = [...(enContent.items || [])];
                      newItems[idx].description = e.target.value;
                      updateField('en', 'items', newItems);
                    }}
                    placeholder="Event description"
                    rows="3"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const newItems = (enContent.items || []).filter((_, i) => i !== idx);
                    updateField('en', 'items', newItems);
                  }}
                  style={{ background: '#ff6b6b', color: 'white', padding: '5px 10px', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => {
                const newItems = [...(enContent.items || []), { year: '', title: '', description: '' }];
                updateField('en', 'items', newItems);
              }}
              style={{ background: '#4CAF50', color: 'white', padding: '8px 15px', border: 'none', borderRadius: '3px', cursor: 'pointer', marginTop: '10px' }}
            >
              + Add Timeline Event
            </button>
          </div>

          <div className="btn-group">
            <button
              onClick={saveSection}
              disabled={!isDirty || isSaving}
              className="btn-primary"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {/* Leadership Section */}
      {activeTab === 'cms-about-leadership' && (
        <div>
          <div className="form-group">
            <label>Leadership Profiles</label>
            <p style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
              Add leadership team members with names, roles, and photos
            </p>
            {(enContent.items || []).map((item, idx) => (
              <div key={idx} style={{ border: '1px solid #ddd', padding: '15px', marginBottom: '15px', borderRadius: '4px' }}>
                <div className="form-group">
                  <label>Name</label>
                  <input
                    type="text"
                    value={item.name || ''}
                    onChange={(e) => {
                      const newItems = [...(enContent.items || [])];
                      newItems[idx].name = e.target.value;
                      updateField('en', 'items', newItems);
                    }}
                    placeholder="Full name"
                    maxLength={100}
                  />
                </div>
                <div className="form-group">
                  <label>Role/Title</label>
                  <input
                    type="text"
                    value={item.role || ''}
                    onChange={(e) => {
                      const newItems = [...(enContent.items || [])];
                      newItems[idx].role = e.target.value;
                      updateField('en', 'items', newItems);
                    }}
                    placeholder="e.g., Principal, Head of Student Life"
                    maxLength={100}
                  />
                </div>
                <div className="form-group">
                  <label>Biography</label>
                  <textarea
                    value={item.bio || ''}
                    onChange={(e) => {
                      const newItems = [...(enContent.items || [])];
                      newItems[idx].bio = e.target.value;
                      updateField('en', 'items', newItems);
                    }}
                    placeholder="Short biography or role description"
                    rows="3"
                  />
                </div>
                <ImageUploadField
                  value={item.photo || ''}
                  onChange={(url) => {
                    const newItems = [...(enContent.items || [])];
                    newItems[idx].photo = url;
                    updateField('en', 'items', newItems);
                  }}
                  label="Profile Photo"
                  folder="about-us/leadership"
                  apiBase={apiBase}
                />
                <button
                  type="button"
                  onClick={() => {
                    const newItems = (enContent.items || []).filter((_, i) => i !== idx);
                    updateField('en', 'items', newItems);
                  }}
                  style={{ background: '#ff6b6b', color: 'white', padding: '5px 10px', border: 'none', borderRadius: '3px', cursor: 'pointer', marginTop: '10px' }}
                >
                  Remove Profile
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => {
                const newItems = [...(enContent.items || []), { name: '', role: '', bio: '', photo: '' }];
                updateField('en', 'items', newItems);
              }}
              style={{ background: '#4CAF50', color: 'white', padding: '8px 15px', border: 'none', borderRadius: '3px', cursor: 'pointer', marginTop: '10px' }}
            >
              + Add Leadership Profile
            </button>
          </div>

          <div className="btn-group">
            <button
              onClick={saveSection}
              disabled={!isDirty || isSaving}
              className="btn-primary"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
